import pageStateService from 'core/PageState.service';
import { withRouter } from 'core/withRouter';
import { AdminDTO, getMenus, RoleDTO } from 'models/admin';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import adminService from 'services/admin.service';
import PageUtil from 'utils/Page.util';
import AdminUpdateV from './Admin.update.v';
import authService from 'services/auth.service';

class Params {
    error: string = null;
    isLoaded: boolean = false;
    roles = RoleDTO.roles();
    admin: AdminDTO = new AdminDTO();
}

function AdminUpdate(props: any) {
    const navigate = useNavigate();
    const [params, setParams] = useState(new Params());
    const username = props?.params?.username;
    const isUpdate: boolean = username ? true : false;
    const isAdmin = useRef(authService.getToken().roles.includes('admin'));
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        console.log('onMount');

        if (isAdmin.current) {
            params.roles = params.roles.filter((x) => x.key == 'admin');
        } else {
            params.roles = params.roles.filter((x) => x.key != 'master');
        }

        getData();

        return () => { };
    }, []);

    // 권한별 기본 메뉴 가져오기
    function getDefaultMenusByRole(roleKey: string): string[] {
        const allMenus = getMenus();

        switch (roleKey) {
            case 'master':
                // 마스터: 모든 메뉴
                return allMenus.map(menu => menu.path);

            case 'admin':
                // 관리자: 상품관리, 출장비관리 제외
                return allMenus
                    .filter(menu => !['product', 'trip-fee'].includes(menu.id))
                    .map(menu => menu.path);

            case 'trip':
                // 출장자: 출장 캘린더만
                return allMenus
                    .filter(menu => menu.id === 'as-calendar')
                    .map(menu => menu.path);

            case 'normal':
                // 일반: 견적관리 제외
                return allMenus
                    .filter(menu => !["as-analytics", "estimate", "repair-statistics", "agent-stats", 'product', 'trip-fee', 'admin'].includes(menu.id))
                    .map(menu => menu.path);

            default:
                return [];
        }
    }

    // 전화번호 입력 함수
    function onHandlePhone(value: string) {
        const onlyNumbers = value.replace(/\D/g, '');
        const curParams = PageUtil.deepCopy(params) as Params;

        if (onlyNumbers.length < 4) {
            curParams.admin.phone = onlyNumbers;
        } else if (onlyNumbers.length < 7) {
            curParams.admin.phone = `${onlyNumbers.slice(0, 3)}-${onlyNumbers.slice(3)}`;
        } else if (onlyNumbers.length < 11) {
            curParams.admin.phone = `${onlyNumbers.slice(0, 3)}-${onlyNumbers.slice(3, 6)}-${onlyNumbers.slice(6)}`;
        } else {
            curParams.admin.phone = `${onlyNumbers.slice(0, 3)}-${onlyNumbers.slice(3, 7)}-${onlyNumbers.slice(7, 11)}`;
        }

        setParams(curParams);
    }

    function onHandleRoleChange(e: any, roleKey: string): void {
        if (params.admin.roles.includes('master')) return;

        const curParams = PageUtil.deepCopy(params) as Params;

        // 기존 체크 상태 초기화
        if (curParams.roles.length > 1) {
            curParams.roles.forEach((role) => {
                role._checked = false;
            });
        }

        // 선택된 권한 체크
        const selectedRole = curParams.roles.find((p) => p.key == roleKey);
        if (selectedRole) {
            selectedRole._checked = e.target.checked;

            // 권한 선택 시 자동으로 해당 권한의 기본 메뉴 설정
            if (e.target.checked) {
                curParams.admin.menus = getDefaultMenusByRole(roleKey);
            } else {
                curParams.admin.menus = [];
            }
        }

        setParams(curParams);
    }

    // 메뉴 권한 변경 핸들러
    function onHandleMenuChange(menuPath: string, checked: boolean): void {
        const curParams = PageUtil.deepCopy(params) as Params;

        if (!curParams.admin.menus) {
            curParams.admin.menus = [];
        }

        if (menuPath === 'all') {
            // 전체 선택/해제
            if (checked) {
                curParams.admin.menus = getMenus().map(menu => menu.path);
            } else {
                curParams.admin.menus = [];
            }
        } else {
            // 개별 메뉴 선택/해제
            if (checked) {
                if (!curParams.admin.menus.includes(menuPath)) {
                    curParams.admin.menus.push(menuPath);
                }
            } else {
                curParams.admin.menus = curParams.admin.menus.filter(
                    path => path !== menuPath
                );
            }
        }

        setParams(curParams);
    }

    async function getData() {
        if (username) {
            try {
                const res = await adminService.get(username);
                const curParams = PageUtil.deepCopy(params);
                console.log('admin', res);

                const adminRes = res;
                for (const role of curParams.roles) {
                    role._checked = adminRes.roles.filter((p) => p == role.key).length > 0;
                }

                let admin = Object.assign(new AdminDTO(), adminRes);

                // menuPermissions가 없으면 빈 배열로 초기화
                if (!admin.menus) {
                    admin.menus = [];
                }

                curParams.admin = admin;

                setParams((prev) => ({
                    ...prev,
                    error: null,
                    isLoaded: true,
                    admin: curParams.admin,
                    roles: curParams.roles,
                }));
            } catch (error) {
                console.log('exception ', error);
                setParams((prev) => ({
                    ...prev,
                    isLoaded: true,
                    error: error,
                }));

                PageUtil.handleApiError(error);
            }
        } else {
            // 신규 등록시 기본값 설정
            let admin = new AdminDTO();
            admin.menus = [];
            admin.roles = ["admin"];

            // admin 권한 기본 선택 및 해당 메뉴 자동 설정
            const curParams = PageUtil.deepCopy(params);
            curParams.roles.find(r => r.key === 'admin')._checked = true;
            admin.menus = getDefaultMenusByRole('admin');

            setParams((prev) => ({
                ...prev,
                isLoaded: true,
                admin: admin,
                roles: curParams.roles,
            }));
        }
    }

    async function submit() {
        if (isSubmitting) return;

        const dto = PageUtil.deepCopy(params.admin) as AdminDTO;

        // 선택된 권한 추출
        const selectedRoles = params.roles.filter((p) => p._checked).map((role) => role.key);
        dto.roles = selectedRoles;

        if (params.admin.roles.includes('master')) {
            dto.roles.push('master');
            // master는 모든 메뉴 권한 부여
            dto.menus = getMenus().map(menu => menu.path);
        } else if (selectedRoles.length > 0) {
            // 선택된 권한에 따른 기본 메뉴 설정 (이미 onHandleRoleChange에서 설정됨)
            // 단, 권한이 변경되지 않았고 메뉴가 비어있는 경우를 대비
            if (!dto.menus || dto.menus.length === 0) {
                dto.menus = getDefaultMenusByRole(selectedRoles[0]);
            }
        }

        dto.updatedId = authService.getUserId();
        if (!isUpdate) dto.adminId = authService.getUserId();

        // 유효성 검사
        if (!dto.username) {
            alert('계정ID를 입력해 주세요.');
            return;
        }

        if (!dto.name) {
            alert('이름을 입력해 주세요.');
            return;
        }

        if (!isUpdate && !dto.password) {
            alert('비밀번호를 입력해 주세요.');
            return;
        }

        // if (!dto.email) {
        //     alert('이메일을 입력해 주세요.');
        //     return;
        // }

        // if (!dto.phone) {
        //     alert('전화번호를 입력해 주세요.');
        //     return;
        // }

        if (dto.roles.length < 1) {
            dto.roles = ['admin'];
            dto.menus = getDefaultMenusByRole('admin');
        }

        // 메뉴 권한 검증 (master 제외)
        if (!dto.roles.includes('master') && (!dto.menus || dto.menus.length === 0)) {
            alert('최소 1개 이상의 메뉴 권한을 선택해 주세요.');
            return;
        }

        console.log('submit', params.admin, dto, isUpdate);
        setIsSubmitting(true);

        try {
            await adminService.save(dto, isUpdate ? 'Y' : 'N');
            alert('완료되었습니다.');

            pageStateService.clearPageState();
            navigate('/admin', { replace: true });
        } catch (error) {
            setParams((prev) => ({
                ...prev,
                error: error,
            }));

            PageUtil.handleApiError(error);
        } finally {
            setIsSubmitting(false);
        }
    }

    async function remove() {
        if (isSubmitting) return;
        if (!confirm('삭제하시겠습니까?')) return;

        setIsSubmitting(true);

        try {
            await adminService.remove(username);
            alert('완료되었습니다.');

            pageStateService.clearPageState();
            navigate('/admin', { replace: true });
        } catch (error) {
            setParams((prev) => ({
                ...prev,
                error: error,
            }));

            PageUtil.handleApiError(error);
        } finally {
            setIsSubmitting(false);
        }
    }

    const args = {
        params: params,
        setParams: setParams,
        handleChange: PageUtil.handleChange,
        onHandleRoleChange: onHandleRoleChange,
        onHandlePhone: onHandlePhone,
        onHandleMenuChange: onHandleMenuChange,
        isUpdate: isUpdate,
        submit: submit,
        remove: remove,
    };

    return <AdminUpdateV {...args} />;
}

export default withRouter(AdminUpdate, [adminService]);